import { useEffect, useState } from "react";
import { db } from "../config/firebase";
import { doc, updateDoc } from "firebase/firestore";
import ProductImageUpload from "../components/ProductImageUpload";
import BulkPhotoUpload from "../components/BulkPhotoUpload";
import { isNewActive } from "../utils/promo";
import { getAllProducts, invalidateProductCache } from "../services/dataCache";
import { assignBarcode, clearBarcode, findByBarcode } from "../services/barcodeService";
import { downloadStorageSheet, applyStorageSheet } from "../services/storageSheetService";
import { downloadDiscontinuedSheet, applyDiscontinuedSheet } from "../services/discontinuedSheetService";

const STORAGE_METHODS = ["Dry", "Refrigerated", "Frozen"];

function skuCompare(a, b) {
  const numA = parseInt(a.sku, 10);
  const numB = parseInt(b.sku, 10);
  const aIsNum = !isNaN(numA) && /^\d+/.test(a.sku);
  const bIsNum = !isNaN(numB) && /^\d+/.test(b.sku);
  if (aIsNum && bIsNum) return numA - numB;
  if (aIsNum) return -1;
  if (bIsNum) return 1;
  return a.sku.localeCompare(b.sku);
}

// datetime helpers for the "new until" date input (yyyy-mm-dd)
function toDateInputValue(ts) {
  if (!ts) return "";
  const d = typeof ts.toDate === "function" ? ts.toDate() : new Date(ts);
  if (isNaN(d)) return "";
  const mm = String(d.getMonth() + 1).padStart(2, "0");
  const dd = String(d.getDate()).padStart(2, "0");
  return `${d.getFullYear()}-${mm}-${dd}`;
}

export default function AdminProducts() {
  const [products, setProducts] = useState([]);
  const [search, setSearch] = useState("");
  const [loading, setLoading] = useState(true);
  const [saveError, setSaveError] = useState("");

  useEffect(() => {
    async function load() {
      const all = await getAllProducts(); // session cache — 0 reads on revisit
      setProducts([...all].sort(skuCompare));
      setLoading(false);
    }
    load();
  }, []);

  function handleUploaded(sku, url) {
    setProducts(prev => prev.map(p => p.sku === sku ? { ...p, imageUrl: url } : p));
    invalidateProductCache();
  }

  function patchLocal(sku, patch) {
    setProducts(prev => prev.map(p => p.sku === sku ? { ...p, ...patch } : p));
  }

  async function saveField(sku, patch, revert) {
    setSaveError("");
    patchLocal(sku, patch);
    try {
      await updateDoc(doc(db, "products", sku), patch);
      invalidateProductCache(); // other pages refetch fresh data
    } catch (err) {
      patchLocal(sku, revert);
      setSaveError(`Could not save change for SKU ${sku}: ${err.message}`);
    }
  }

  function handleStorageChange(sku, value) {
    const previous = products.find(p => p.sku === sku)?.storageMethod || "";
    saveField(sku, { storageMethod: value }, { storageMethod: previous });
  }

  // "New" flag (backlog #5): checkbox + optional auto-expiry date. The badge
  // and the catalog's New Items filter follow isNewActive() in utils/promo.js.
  function handleNewToggle(p, checked) {
    saveField(p.sku, { isNew: checked, ...(checked ? {} : { newUntil: null }) },
      { isNew: !!p.isNew, newUntil: p.newUntil ?? null });
  }

  function handleNewUntil(p, dateStr) {
    const value = dateStr ? new Date(`${dateStr}T23:59:59`) : null;
    saveField(p.sku, { newUntil: value }, { newUntil: p.newUntil ?? null });
  }

  // "In catalog" toggle (Brian, 2026-07-14): manually pull inactive items
  // back into the catalog. forceActive survives the daily CSV upload, which
  // otherwise re-deactivates anything with zero stock.
  function handleCatalogToggle(p, checked) {
    saveField(p.sku, { active: checked, forceActive: checked },
      { active: !!p.active, forceActive: !!p.forceActive });
  }

  // Per-product barcode editing (Brian, 2026-07-14) — view, change, clear.
  const [barcodeEdits, setBarcodeEdits] = useState({});

  async function saveBarcode(p) {
    const raw = (barcodeEdits[p.sku] ?? "").trim();
    if (!raw) return;
    if (p.barcode && String(p.barcode) !== raw) {
      if (!window.confirm(`${p.sku} already has barcode ${p.barcode}.\n\nReplace it with ${raw}?`)) return;
    }
    const holder = findByBarcode(products.filter(x => x.sku !== p.sku), raw);
    if (holder) {
      if (!window.confirm(`Barcode ${raw} is currently on:\n${holder.sku} — ${holder.name}\n\nMove it to ${p.sku}?`)) return;
    }
    setSaveError("");
    try {
      await assignBarcode(p.sku, raw, products);
      setProducts(prev => prev.map(x => {
        if (x.sku === p.sku) return { ...x, barcode: raw, barcodeSource: "portal" };
        if (holder && x.sku === holder.sku) { const { barcode, barcodeSource, ...rest } = x; return rest; }
        return x;
      }));
      setBarcodeEdits(prev => { const n = { ...prev }; delete n[p.sku]; return n; });
    } catch (err) {
      setSaveError(`Could not save barcode for SKU ${p.sku}: ${err.message}`);
    }
  }

  async function removeBarcode(p) {
    if (!window.confirm(`Remove barcode ${p.barcode} from ${p.sku} — ${p.name}?`)) return;
    setSaveError("");
    try {
      await clearBarcode(p.sku);
      setProducts(prev => prev.map(x => {
        if (x.sku !== p.sku) return x;
        const { barcode, barcodeSource, ...rest } = x;
        return rest;
      }));
    } catch (err) {
      setSaveError(`Could not remove barcode for SKU ${p.sku}: ${err.message}`);
    }
  }

  // Bulk storage-method sheet (Brian, 2026-08-20): download a CSV of all
  // products, type r/f/d in the Storage column in Excel, upload it back.
  // Blank cells leave the product unchanged; the report below lists anything
  // the upload didn't understand so nothing fails silently.
  const [sheetBusy, setSheetBusy] = useState(false);
  const [sheetReport, setSheetReport] = useState(null); // report object or { error }

  async function handleSheetUpload(e) {
    const file = e.target.files?.[0];
    e.target.value = ""; // allow re-uploading the same filename
    if (!file) return;
    setSheetBusy(true);
    setSheetReport(null);
    try {
      const { report, changes } = await applyStorageSheet(file, products);
      // Reflect the new methods in the on-screen list without a refetch.
      if (changes.length > 0) {
        const bySku = new Map(changes.map(c => [c.sku, c.method]));
        setProducts(prev => prev.map(p =>
          bySku.has(p.sku) ? { ...p, storageMethod: bySku.get(p.sku) } : p));
      }
      setSheetReport(report);
    } catch (err) {
      setSheetReport({ error: err.message });
    } finally {
      setSheetBusy(false);
    }
  }

  // Discontinued sheet (Brian, 2026-08-20): same download/mark/upload flow
  // as storage methods — x = gone for good (hidden from catalog browsing,
  // auto-revived by the daily CSV if stock returns).
  const [discBusy, setDiscBusy] = useState(false);
  const [discReport, setDiscReport] = useState(null);

  async function handleDiscUpload(e) {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    setDiscBusy(true);
    setDiscReport(null);
    try {
      const { report, changes } = await applyDiscontinuedSheet(file, products);
      if (changes.length > 0) {
        const bySku = new Map(changes.map(c => [c.sku, c]));
        setProducts(prev => prev.map(p => {
          const c = bySku.get(p.sku);
          return c ? { ...p, discontinued: c.discontinued, keepHidden: c.keepHidden } : p;
        }));
      }
      setDiscReport(report);
    } catch (err) {
      setDiscReport({ error: err.message });
    } finally {
      setDiscBusy(false);
    }
  }

  const filtered = products.filter(p =>
    p.name.toLowerCase().includes(search.toLowerCase()) ||
    p.sku.includes(search)
  );

  if (loading) return <p>Loading products...</p>;

  return (
    <div className="admin-products">
      <h2>Products ({products.filter(p => p.imageUrl).length} / {products.length} photos uploaded)</h2>
      <BulkPhotoUpload products={products} onUploaded={handleUploaded} />

      <div className="storage-sheet-panel">
        <p className="order-for-label">
          🌡 Storage methods — bulk edit by spreadsheet
          ({products.filter(p => !p.storageMethod && !p.discontinued && !p.keepHidden).length} of {products.filter(p => !p.discontinued && !p.keepHidden).length} not set)
        </p>
        <p className="inventory-help">
          Download the sheet, type <strong>r</strong> (refrigerated), <strong>f</strong> (frozen)
          or <strong>d</strong> (dry) in the Storage column in Excel, save, and upload it back.
          Items with no storage method are at the TOP of the sheet. Blank = leave unchanged.
          Don't reorder the columns.
        </p>
        <button
          className="mini-btn"
          disabled={sheetBusy}
          onClick={() => downloadStorageSheet(products)}
        >⬇ Download storage sheet</button>
        {" "}
        <label className="mini-btn" style={{ cursor: sheetBusy ? "wait" : "pointer" }}>
          {sheetBusy ? "Applying..." : "⬆ Upload filled sheet"}
          <input
            type="file"
            accept=".csv,text/csv"
            style={{ display: "none" }}
            disabled={sheetBusy}
            onChange={handleSheetUpload}
          />
        </label>
        {sheetReport && (
          sheetReport.error ? (
            <p className="error">{sheetReport.error}</p>
          ) : (
            <div className="inventory-help">
              <strong>
                Updated {sheetReport.updated} · already correct {sheetReport.unchanged} · left blank {sheetReport.skippedBlank}
              </strong>
              {sheetReport.unknownValues.length > 0 && (
                <p className="error">
                  Didn't understand the Storage value on {sheetReport.unknownValues.length} row{sheetReport.unknownValues.length === 1 ? "" : "s"} (use r, f, or d):{" "}
                  {sheetReport.unknownValues.slice(0, 10).map(u => `${u.sku} → "${u.value}"`).join(", ")}
                  {sheetReport.unknownValues.length > 10 ? ` … and ${sheetReport.unknownValues.length - 10} more` : ""}
                </p>
              )}
              {sheetReport.unknownSkus.length > 0 && (
                <p className="error">
                  {sheetReport.unknownSkus.length} SKU{sheetReport.unknownSkus.length === 1 ? "" : "s"} not found in the portal (skipped):{" "}
                  {sheetReport.unknownSkus.slice(0, 10).join(", ")}
                  {sheetReport.unknownSkus.length > 10 ? ` … and ${sheetReport.unknownSkus.length - 10} more` : ""}
                </p>
              )}
            </div>
          )
        )}
      </div>

      <div className="storage-sheet-panel">
        <p className="order-for-label">
          🚫 Discontinued items — bulk edit by spreadsheet
          ({products.filter(p => p.discontinued).length} marked · {products.filter(p => p.keepHidden).length} kept hidden)
        </p>
        <p className="inventory-help">
          The sheet lists every out-of-stock item in the carried SKU ranges (1–5, SLP, w),
          plus anything already marked.
          Type <strong>x</strong> next to items that are gone for good — they disappear from
          catalog browsing (search and barcode scan still find them), and revive automatically
          when the daily QB upload shows stock again.
          Type <strong>xx</strong> to KEEP an item hidden — customers can't see it at all
          (no browse, search, scan, or reorder), even when it has stock; staff still find it
          by search. An xx only clears when you blank it here and re-upload.
          Blank = still carried.
        </p>
        <button
          className="mini-btn"
          disabled={discBusy}
          onClick={() => downloadDiscontinuedSheet(products)}
        >⬇ Download discontinued sheet</button>
        {" "}
        <label className="mini-btn" style={{ cursor: discBusy ? "wait" : "pointer" }}>
          {discBusy ? "Applying..." : "⬆ Upload marked sheet"}
          <input
            type="file"
            accept=".csv,text/csv"
            style={{ display: "none" }}
            disabled={discBusy}
            onChange={handleDiscUpload}
          />
        </label>
        {discReport && (
          discReport.error ? (
            <p className="error">{discReport.error}</p>
          ) : (
            <div className="inventory-help">
              <strong>
                Marked discontinued {discReport.marked} · kept hidden {discReport.hidden} · un-marked {discReport.cleared} · unchanged {discReport.unchanged}
              </strong>
              {discReport.unknownValues.length > 0 && (
                <p className="error">
                  Didn't understand the value on {discReport.unknownValues.length} row{discReport.unknownValues.length === 1 ? "" : "s"} (use x, xx, or leave blank):{" "}
                  {discReport.unknownValues.slice(0, 10).map(u => `${u.sku} → "${u.value}"`).join(", ")}
                  {discReport.unknownValues.length > 10 ? ` … and ${discReport.unknownValues.length - 10} more` : ""}
                </p>
              )}
              {discReport.unknownSkus.length > 0 && (
                <p className="error">
                  {discReport.unknownSkus.length} SKU{discReport.unknownSkus.length === 1 ? "" : "s"} not found in the portal (skipped):{" "}
                  {discReport.unknownSkus.slice(0, 10).join(", ")}
                  {discReport.unknownSkus.length > 10 ? ` … and ${discReport.unknownSkus.length - 10} more` : ""}
                </p>
              )}
            </div>
          )
        )}
      </div>
      <input
        placeholder="Search by name or SKU..."
        value={search}
        onChange={(e) => setSearch(e.target.value)}
      />
      {saveError && <p className="error">{saveError}</p>}
      <div className="admin-product-list">
        {filtered.map(p => (
          <div key={p.sku} className="admin-product-row">
            <div className="admin-product-info">
              <p className="sku">SKU: {p.sku}</p>
              <p className="name">
                {p.name}
                {isNewActive(p) && <span className="new-badge">NEW</span>}
              </p>
              <label className="catalog-toggle">
                <input
                  type="checkbox"
                  checked={!!p.active}
                  onChange={(e) => handleCatalogToggle(p, e.target.checked)}
                />
                In catalog{p.forceActive && !isNaN(p.stockQty) && p.stockQty <= 0 ? " (kept despite 0 stock)" : ""}
              </label>
              <div className="barcode-edit-row">
                <input
                  className="barcode-edit-input"
                  placeholder="Barcode"
                  value={barcodeEdits[p.sku] ?? (p.barcode || "")}
                  onChange={(e) => setBarcodeEdits(prev => ({ ...prev, [p.sku]: e.target.value }))}
                />
                <button
                  className="mini-btn"
                  disabled={barcodeEdits[p.sku] === undefined || !(barcodeEdits[p.sku] ?? "").trim() || (barcodeEdits[p.sku] ?? "").trim() === String(p.barcode || "")}
                  onClick={() => saveBarcode(p)}
                >Save</button>
                {p.barcode && (
                  <button className="mini-btn danger" onClick={() => removeBarcode(p)}>Clear</button>
                )}
              </div>
            </div>
            <label className="storage-select-label">
              Storage
              <select
                className="storage-select"
                value={p.storageMethod || ""}
                onChange={(e) => handleStorageChange(p.sku, e.target.value)}
              >
                <option value="">— not set —</option>
                {STORAGE_METHODS.map(m => <option key={m} value={m}>{m}</option>)}
              </select>
            </label>
            <div className="new-item-controls">
              <label className="new-item-checkbox">
                <input
                  type="checkbox"
                  checked={!!p.isNew}
                  onChange={(e) => handleNewToggle(p, e.target.checked)}
                />
                New
              </label>
              {p.isNew && (
                <label className="new-until-label">
                  until
                  <input
                    type="date"
                    value={toDateInputValue(p.newUntil)}
                    onChange={(e) => handleNewUntil(p, e.target.value)}
                  />
                </label>
              )}
            </div>
            <ProductImageUpload
              sku={p.sku}
              currentImageUrl={p.imageUrl}
              onUploaded={(url) => handleUploaded(p.sku, url)}
            />
          </div>
        ))}
      </div>
    </div>
  );
}
